import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Globe2, GraduationCap, ClipboardList, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

export default function Home() {
  return (
    <div className="space-y-12 lg:space-y-16">
      {/* Hero section in the style of the reference template */}
      <section className="relative overflow-hidden rounded-3xl border border-[#ddcaa4] bg-[#f9f2e6] px-5 py-8 shadow-sm lg:px-10 lg:py-10">
        {/* Logo watermark in the background */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-10 sm:opacity-15">
          <img
            src="https://customer-assets.emergentagent.com/job_global-music-exams/artifacts/a1h5nluh_GMC-Logo-removbg.jpg"
            alt=""
            aria-hidden="true"
            className="max-h-[260px] w-auto object-contain"
          />
        </div>

        <div className="relative space-y-6 text-center">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#b39254]">
              Examinations · Certifications · Global Recognition
            </p>
            <h1 className="text-3xl font-semibold tracking-tight text-[#182033] sm:text-4xl lg:text-[2.65rem] lg:leading-tight">
              International standards in music certification.
            </h1>
            <p className="mx-auto max-w-2xl text-sm leading-relaxed text-[#4b4a46] sm:text-base">
              Global Music Conservatory offers structured, level-based music
              examinations, aligning local teaching with internationally oriented
              conservatory expectations.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="h-px w-32 bg-gradient-to-r from-transparent via-[#d6c29e] to-transparent" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              className="bg-[#1b2236] px-8 text-xs font-semibold uppercase tracking-[0.18em] text-[#f8f2e6] hover:bg-[#151b2b]"
            >
              <Link to="/examination-system">Learn more</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-[#d9c7a3] bg-[#fdf8ef] px-8 text-xs font-semibold uppercase tracking-[0.18em] text-[#1f2738] hover:bg-[#f5ebd9]"
            >
              <Link to="/certification-recognition">Certifications</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Music Scholarship Banner */}
      <Link 
        to="/music-scholarship"
        className="block group"
      >
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#182033] via-[#1e293b] to-[#0f172a] px-6 py-4 shadow-md transition-all hover:shadow-lg hover:scale-[1.01]">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 h-20 w-20 rounded-full bg-amber-400/20 blur-2xl"></div>
            <div className="absolute bottom-0 right-1/4 h-16 w-16 rounded-full bg-blue-400/20 blur-xl"></div>
          </div>
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600">
                <Award className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-white sm:text-xl">Apply for Academic Scholarship</p>
                <p className="text-sm text-[#d5c39d]">Scholarships from €300 to €3,000 for talented students worldwide</p>
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-all group-hover:bg-amber-600">
                Learn More →
              </span>
            </div>
          </div>
        </div>
      </Link>

      {/* About the Conservatory section */}
      <section className="space-y-8">
        <div className="space-y-3 text-center">
          <h2 className="text-xl font-semibold tracking-tight text-[#182033]">
            About the Conservatory
          </h2>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-[#4b4a46] sm:text-base">
            Global Music Conservatory upholds formal standards in music assessment.
            Established in 2008, the Conservatory has been awarding music certifications
            for over 15 years and has awarded numerous certifications to music learners
            around the world. Its examinations and certifications are intended to provide
            clear, comprehensive evaluations for students and teachers across a range of
            instruments and levels.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <AboutItem
            title="Academic excellence"
            description="Structured syllabi and externally moderated examinations support consistent, conservatory-style training."
          />
          <AboutItem
            title="Internationally oriented"
            description="Level descriptors and assessment principles are designed to be understood by institutions worldwide."
          />
          <AboutItem
            title="Conservatory tradition"
            description="Emphasis on musical understanding, secure technique and stylistic awareness within a formal assessment setting."
          />
        </div>
      </section>

      {/* Student Testimonials Section */}
      <section className="space-y-6">
        <div className="space-y-3 text-center">
          <h2 className="text-xl font-semibold tracking-tight text-[#182033]">
            What Our Students Say
          </h2>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-[#4b4a46] sm:text-base">
            Hear from certified musicians who have achieved their goals through
            Global Music Conservatory examinations.
          </p>
        </div>

        <div className="relative overflow-hidden">
          <div className="flex animate-scroll gap-6 py-4">
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <TestimonialCard key={`${testimonial.name}-${index}`} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Examinations & certifications section */}
      <section className="space-y-6">
        <div className="space-y-3 text-center">
          <h2 className="text-xl font-semibold tracking-tight text-[#182033]">
            Examinations &amp; Certifications
          </h2>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-[#4b4a46] sm:text-base">
            The certification framework covers instrumental, vocal and musicianship
            components, allowing programmes of study to be aligned with clearly
            articulated outcomes.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <ExamTile
            title="Instrumental examinations"
            description="Piano, strings, guitar and drum kit examinations from Level 1 to Level 6."
            link="/instruments-levels"
          />
          <ExamTile
            title="Vocal examinations"
            description="Vocals (Western Contemporary / Indian Classical) with options for Bollywood playback repertoire."
            link="/instruments-levels"
          />
          <ExamTile
            title="Theory &amp; musicianship"
            description="Written and practical musicianship components that support aural work and theoretical understanding."
            link="/assessment-criteria"
          />
        </div>
      </section>

      {/* Why choose Global Music Conservatory section */}
      <section className="space-y-6 rounded-3xl border border-[#ddcaa4] bg-[#f9f2e6] px-5 py-7 shadow-sm lg:px-8">
        <div className="space-y-2 text-center">
          <h2 className="text-xl font-semibold tracking-tight text-[#182033]">
            Why choose Global Music Conservatory
          </h2>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <WhyItem
            icon={Globe2}
            title="Global standards"
            description="A level-based structure that can be understood across institutions and regions."
          />
          <WhyItem
            icon={ShieldCheck}
            title="Transparent assessment"
            description="Published criteria and feedback that support planning the next stage of study."
          />
          <WhyItem
            icon={GraduationCap}
            title="Pedagogical support"
            description="Resources and guidance for teachers integrating examinations into long-term learning."
          />
          <WhyItem
            icon={ClipboardList}
            title="Recognised achievement"
            description="Certificates that clearly state instrument, level and mode of assessment."
          />
        </div>
      </section>
    </div>
  );
}

function AboutItem({ title, description }) {
  return (
    <div className="rounded-2xl border border-[#e1d2b3] bg-[#fdf8ef] px-4 py-5 text-left shadow-sm">
      <p className="text-sm font-semibold tracking-tight text-[#182033]">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-[#4b4a46]">{description}</p>
    </div>
  );
}

function ExamTile({ title, description, link }) {
  return (
    <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm transition hover:-translate-y-0.5 hover:border-[#d3be96]">
      <CardContent className="space-y-3 px-4 py-5 text-sm text-[#4b4a46]">
        <p className="text-sm font-semibold tracking-tight text-[#182033]">{title}</p>
        <p>{description}</p>
        <Button
          asChild
          variant="outline"
          className="mt-1 h-8 border-[#d5c39d] bg-white px-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#1f2738] hover:bg-[#f5ebd9]"
        >
          <Link to={link}>Learn more</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function WhyItem({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[#e1d2b3] bg-white/80 px-4 py-5 text-left shadow-sm">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#f4e5c6] text-[#1f2738]">
          <Icon className="h-4 w-4" />
        </span>
        <p className="text-sm font-semibold tracking-tight text-[#182033]">{title}</p>
      </div>
      <p className="text-sm leading-relaxed text-[#4b4a46]">{description}</p>
    </div>
  );
}

const testimonials = [
  {
    name: "Arjun Mehta",
    location: "Mumbai, India",
    instrument: "Guitar",
    level: "Level 5",
    testimonial: "Passing my Guitar Level 5 certification was a dream come true. The structured syllabus helped me develop technique I never thought possible. The certificate opened doors for teaching opportunities across India.",
  },
  {
    name: "Emma Richardson",
    location: "London, UK",
    instrument: "Piano",
    level: "Level 6",
    testimonial: "The Piano Mastery certification validated years of dedication. The examination process was thorough yet encouraging. I now teach at a conservatory and my GMC certificate is proudly displayed.",
  },
  {
    name: "Priya Sharma",
    location: "Delhi, India",
    instrument: "Violin",
    level: "Level 4",
    testimonial: "The Violin certification programme transformed my playing. The detailed feedback from examiners helped me understand my strengths and areas for growth. A truly rewarding experience!",
  },
  {
    name: "Michael Chen",
    location: "Singapore",
    instrument: "Drums",
    level: "Level 5",
    testimonial: "From basic rhythms to complex polyrhythms, the Drum Kit syllabus pushed me to become a complete drummer. The certification gave me credibility in the professional music scene.",
  },
  {
    name: "Kavya Krishnan",
    location: "Chennai, India",
    instrument: "Hindustani Vocals",
    level: "Level 5",
    testimonial: "The Hindustani Classical Vocals certification honoured traditional training while providing international recognition. My raga knowledge and tala discipline improved tremendously through this journey.",
  },
  {
    name: "David Mueller",
    location: "Berlin, Germany",
    instrument: "Alto Saxophone",
    level: "Level 4",
    testimonial: "The Alto Saxophone programme covered everything from embouchure to improvisation. Passing Level 4 gave me the confidence to perform jazz professionally. Highly recommended!",
  },
];

function TestimonialCard({ name, location, instrument, level, testimonial }) {
  return (
    <div className="flex-shrink-0 w-[320px] rounded-2xl border border-[#e1d2b3] bg-[#fdf8ef] p-5 shadow-sm">
      <div className="mb-4">
        <p className="text-sm font-semibold text-[#182033]">{name}</p>
        <p className="text-xs text-[#6b6a66]">{location}</p>
        <p className="text-xs font-medium text-[#8c7648]">{instrument} • {level}</p>
      </div>
      <p className="text-sm leading-relaxed text-[#4b4a46] italic">"{testimonial}"</p>
    </div>
  );
}
